import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { prefixes, useTypedTranslation } from "@/locales";
import React, { useEffect, useState } from 'react';
import { SearchLg } from '@untitledui/icons';
import { Button } from '@/components/_untitled/base/buttons/button';
import { Input } from '@/components/_untitled/base/input/input';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { useScrollWithHysteresis } from '@/hooks/useScrollWithHysteresis';
import { Link } from '@/components/navigation/Link';
import { ROUTES } from '@/config/routes';
import { LanguageToggle } from '@/components/global/LanguageToggle';
import { PublicNavAccountCard } from '@/components/ui/PublicNavAccountCard';
import { getActiveSteps } from '@/features/auth/config/kycSteps.config';
import { MonabidLogo } from "@/components/monabid-logo.tsx";
import { BidIcon } from "@/assets/Buttons/bid-icon.tsx";
import { MyBidsSlideout } from "@/components/domain/bid/MyBidsSlideout.tsx";

export const Header = () => {
    const [tHeader, tLogin] = useTypedTranslation(prefixes('header', 'auth.login'));
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, logout, user } = useAuth();
    const { isAdmin } = useAdminAccess();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = useState('');
    const isScrolled = useScrollWithHysteresis();

    // Sync search value with URL parameter (only when URL changes externally)
    useEffect(() => {
        const searchParam = searchParams.get('search') || '';
        setSearchValue(searchParam);
    }, [searchParams]);

    // Update URL as user types (debounced)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (searchValue.trim()) {
                    next.set('search', searchValue.trim());
                } else {
                    next.delete('search');
                }
                // Reset to page 1 when searching
                if (searchValue.trim() !== (prev.get('search') || '')) {
                    next.delete('page');
                }
                return next;
            }, { replace: true });
        }, 300); // 300ms debounce

        return () => clearTimeout(timeoutId);
    }, [searchValue, setSearchParams]);

    const handleLogout = async () => {
        try {
            await logout();
            // Context-dependent redirect: if on protected route, go home
            if (location.pathname.startsWith('/admin')) {
                navigate(ROUTES.HOME);
            }
            // Public pages: stay in place (no navigation needed)
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const handleSettingsClick = () => {
        navigate(ROUTES.SETTINGS);
    };

    const handleAdminModeClick = () => {
        navigate(ROUTES.ADMIN);
    };

    const userData = user
        ? {
            name: `${ user.firstName || '\u00A0' } ${ user.lastName || '\u00A0' }`,
            email: user.email,
            avatar: user.profilePicture || '',
        }
        : undefined;

    const kycProgress = user
        ? {
            completed: getActiveSteps().filter((step) => step.validation(user)).length,
            total: getActiveSteps().length,
        }
        : undefined;

    const handleKycClick = () => {
        // Add query param to trigger KycModalManager
        const newParams = new URLSearchParams(searchParams);
        newParams.set('openKycModal', 'true');
        setSearchParams(newParams);
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const isOnHomePage = location.pathname === ROUTES.HOME;

            // Only navigate if not on home page (home page updates automatically via effect)
            if (!isOnHomePage) {
                const params = new URLSearchParams();
                if (searchValue.trim()) {
                    params.set('search', searchValue.trim());
                }
                navigate(`${ ROUTES.HOME }?${ params.toString() }`);
            }
        }
    };

    return (
        <header className="sticky top-0 z-50 w-full bg-white border-b border-border-secondary transition-all">
            {/* Top Utility Bar */ }
            <div className={ `bg-gray-900 px-4 sm:px-10 transition-all ${ isScrolled ? 'py-1' : 'py-1.5 sm:py-2.5' }` }>
                <div className="flex items-center justify-end gap-6">
                    {/* Currency */ }
                    <span className="text-white text-sm">USD</span>

                    {/* Language Selector */ }
                    <LanguageToggle variant="header"/>
                </div>
            </div>

            {/* Main Navigation */ }
            <nav className={ `flex flex-wrap px-4 sm:px-10 transition-all ${ isScrolled ? 'py-2' : 'py-2 sm:py-6' }` }>
                {/* Logo - full width on mobile */ }
                <div className="w-full sm:w-auto flex items-center mb-4 sm:mb-0">
                    <Link href={ ROUTES.HOME } className="focus:outline-none shrink-0">
                        <MonabidLogo/>
                    </Link>
                </div>

                {/* Container for Search + Auth */ }
                {/* Mobile: full width with justify-between */ }
                {/* Desktop: flex-1 with normal layout */ }
                <div
                    className="w-full sm:flex-1 flex items-center justify-between sm:justify-start gap-3 sm:gap-8 sm:ms-8">
                    {/* Search Bar */ }
                    <div className="flex-1 sm:max-w-xl">
                        <Input
                            icon={ SearchLg }
                            placeholder={ tHeader('searchPlaceholder') }
                            aria-label={ tHeader('search') }
                            value={ searchValue }
                            onChange={ setSearchValue }
                            onKeyDown={ handleSearchKeyDown }
                            className="w-full"
                        />
                    </div>

                    {/* Auth Section */ }
                    <div className="flex items-center gap-3 shrink-0 sm:ms-auto">
                        { isAuthenticated && userData ? (
                            <>
                                <Link
                                    slideout={ { title: tHeader('myBids'), content: <MyBidsSlideout/> } }
                                    className="p-2 cursor-pointer relative"
                                    aria-label={ tHeader('myBids') }
                                >
                                    <BidIcon/>
                                    { user?.hasUnreadUpdates && (
                                        <span className="absolute top-1 end-1 w-2 h-2 bg-error-500 rounded-full"/>
                                    ) }
                                </Link>
                                <PublicNavAccountCard
                                    user={ userData }
                                    onSettingsClick={ handleSettingsClick }
                                    onAdminModeClick={ isAdmin ? handleAdminModeClick : undefined }
                                    onKycClick={ handleKycClick }
                                    kycProgress={ kycProgress }
                                    onSignOut={ handleLogout }
                                />
                            </>
                        ) : (
                            <div className="flex gap-3">
                                <Link href={ ROUTES.SIGN_UP }>
                                    <Button color="secondary" size="sm">
                                        { tLogin('signUp') }
                                    </Button>
                                </Link>
                                <Link href={ ROUTES.LOGIN }>
                                    <Button color="primary" size="sm">
                                        { tLogin('logIn') }
                                    </Button>
                                </Link>
                            </div>
                        ) }
                    </div>
                </div>
            </nav>
        </header>
    );
};